import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { buildPages, notFoundPage, absoluteUrl } from "../seo/meta.mjs";
import profile from "../_data/profile.json";
import projects from "../_data/projects.json";
import resume from "../_data/resume.json";
import home from "../_data/home.json";
import ru from "../i18n/ru.json";

const pages = buildPages({ profile, projects, resume, home, ru });
const fallback = notFoundPage(profile);

// Finds or creates a <meta>/<link> tag and sets its value
const setTag = (tag, keyAttr, key, valueAttr, value) => {
  let el = document.head.querySelector(`${tag}[${keyAttr}="${key}"]`);
  if (!el) {
    el = document.createElement(tag);
    el.setAttribute(keyAttr, key);
    document.head.appendChild(el);
  }
  el.setAttribute(valueAttr, value);
};

/**
 * Keeps <head> in sync with the current route during client-side navigation.
 * The initial HTML for each route is already prerendered with the same data
 * by scripts/prerender.mjs, so crawlers get correct tags without JavaScript.
 *
 * @component
 */
const Seo = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const path = pathname !== "/" ? pathname.replace(/\/+$/, "") : "/";
    const page = pages.find((p) => p.path === path) || fallback;
    const url = absoluteUrl(page.path);
    const image = absoluteUrl(page.image);

    document.title = page.title;
    document.documentElement.lang = page.lang || "en";

    // hreflang alternates (only the home page has a Russian version)
    document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
    (page.alternates || []).forEach(([lang, path]) => {
      const link = document.createElement("link");
      link.rel = "alternate";
      link.hreflang = lang;
      link.href = absoluteUrl(path);
      document.head.appendChild(link);
    });
    setTag("meta", "name", "description", "content", page.description);
    setTag("meta", "name", "robots", "content", page.noindex ? "noindex, follow" : "index, follow, max-image-preview:large");
    setTag("link", "rel", "canonical", "href", url);
    setTag("meta", "property", "og:title", "content", page.title);
    setTag("meta", "property", "og:description", "content", page.description);
    setTag("meta", "property", "og:url", "content", url);
    setTag("meta", "property", "og:type", "content", page.type);
    setTag("meta", "property", "og:image", "content", image);
    setTag("meta", "name", "twitter:title", "content", page.title);
    setTag("meta", "name", "twitter:description", "content", page.description);
    setTag("meta", "name", "twitter:image", "content", image);

    let ld = document.getElementById("ld-json");
    if (page.jsonLd) {
      if (!ld) {
        ld = document.createElement("script");
        ld.type = "application/ld+json";
        ld.id = "ld-json";
        document.head.appendChild(ld);
      }
      ld.textContent = JSON.stringify(page.jsonLd);
    } else if (ld) {
      ld.remove();
    }
  }, [pathname]);

  return null;
};

export default Seo;
